/**
 * Timeline Export Module
 * 
 * Exports the timeline as an MP4 video with audio using WebCodecs.
 * 
 * Usage:
 *   import { exportTimeline, isExportSupported } from './export';
 *   
 *   if (isExportSupported()) {
 *     const blob = await exportTimeline(timelineData, {
 *       width: 1920,
 *       height: 1080,
 *       fps: 30,
 *       onProgress: (progress) => console.log(`${progress * 100}%`),
 *     });
 *     
 *     // Download the file
 *     downloadBlob(blob, 'export.mp4');
 *   }
 */

import { isEncodingSupported, VideoEncoderWrapper, AudioEncoderWrapper } from './encoder';
import { createRenderer } from './renderer';
import { getFadeGain } from '../store';

// Re-export utility functions
export { isEncodingSupported } from './encoder';

/**
 * Check if export is supported in the current browser
 */
export function isExportSupported() {
  return isEncodingSupported();
}

/**
 * Export options
 * @typedef {Object} ExportOptions
 * @property {number} [width=1920] - Output video width
 * @property {number} [height=1080] - Output video height
 * @property {number} [fps=30] - Output video framerate
 * @property {number} [videoBitrate=5000000] - Video bitrate in bps
 * @property {number} [audioBitrate=128000] - Audio bitrate in bps
 * @property {number} [sampleRate=48000] - Audio sample rate
 * @property {Function} [onProgress] - Progress callback (0-1)
 * @property {Function} [onStatus] - Status message callback
 * @property {AbortSignal} [signal] - AbortSignal for cancellation
 */

/**
 * Export the timeline as an MP4 video with audio
 * 
 * @param {Object} timeline - Timeline data from store.toJSON()
 * @param {ExportOptions} options - Export options
 * @returns {Promise<Blob>} The exported MP4 file
 */
export async function exportTimeline(timeline, options = {}) {
  const {
    width = 1920,
    height = 1080,
    fps = 30,
    videoBitrate = 5_000_000,
    audioBitrate = 128_000,
    sampleRate = 48000,
    channels = 2,
    onProgress = () => {},
    onStatus = () => {},
    signal,
  } = options;

  // Validate
  if (!isExportSupported()) {
    throw new Error('WebCodecs encoding is not supported in this browser');
  }

  const { tracks, clips, assets, mutedTracks = {}, trackVolumes = {} } = timeline;
  
  if (!clips || clips.length === 0) {
    throw new Error('No clips to export');
  }

  onStatus('Initializing...');

  // Create renderer for video
  const renderer = createRenderer({ width, height, fps });

  // Encoded chunks storage
  const videoChunks = [];
  const audioChunks = [];

  // Create video encoder
  const videoEncoder = new VideoEncoderWrapper({
    width,
    height,
    fps,
    bitrate: videoBitrate,
    onChunk: (chunk, meta) => {
      videoChunks.push({ chunk, meta });
    },
    onError: (e) => {
      console.error('Video encoder error:', e);
    },
  });

  // Create audio encoder
  const audioEncoder = new AudioEncoderWrapper({
    sampleRate,
    channels,
    bitrate: audioBitrate,
    onChunk: (chunk, meta) => {
      audioChunks.push({ chunk, meta });
    },
    onError: (e) => {
      console.error('Audio encoder error:', e);
    },
  });

  // Audio mixing state
  const decodedAudio = new Map(); // assetId -> AudioBuffer

  try {
    // Initialize encoders
    onStatus('Initializing encoders...');
    await videoEncoder.init();
    await audioEncoder.init();

    // Preload video/image assets
    onStatus('Loading media assets...');
    await renderer.preloadAssets(assets, (p) => {
      onProgress(p * 0.05); // 0-5% for preloading video
    });

    // Preload and decode audio from all clips (excluding muted tracks)
    onStatus('Decoding audio...');
    const audioClips = getAudioClips(clips, tracks, assets, mutedTracks);
    
    for (let i = 0; i < audioClips.length; i++) {
      const clip = audioClips[i];
      const asset = assets[clip.assetId];
      
      if (!decodedAudio.has(clip.assetId)) {
        try {
          const audioBuffer = await decodeAudioAsset(asset.url, sampleRate);
          decodedAudio.set(clip.assetId, audioBuffer);
        } catch (e) {
          console.warn(`Failed to decode audio for asset ${clip.assetId}:`, e);
        }
      }
      
      onProgress(0.05 + (i / audioClips.length) * 0.05); // 5-10% for audio decode
    }

    // Check for cancellation
    if (signal?.aborted) {
      throw new DOMException('Export cancelled', 'AbortError');
    }

    // Calculate timeline duration
    const duration = renderer.getTimelineDuration(clips);
    const totalFrames = Math.ceil(duration * fps);
    
    // Audio encoding parameters
    const samplesPerFrame = Math.floor(sampleRate / fps);
    const audioFrameSize = 1024; // AAC frame size
    
    onStatus(`Rendering...`);

    // Render and encode each frame (video + audio)
    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex++) {
      // Check for cancellation
      if (signal?.aborted) {
        throw new DOMException('Export cancelled', 'AbortError');
      }

      const time = frameIndex / fps;
      
      // === VIDEO ===
      const frame = await renderer.renderFrame(time, { tracks, clips, assets });
      const isKeyFrame = frameIndex % (fps * 2) === 0;
      videoEncoder.encode(frame, { keyFrame: isKeyFrame });
      frame.close();

      // === AUDIO ===
      // Mix audio for this frame's time range
      const audioStartTime = time;
      const audioEndTime = Math.min(time + 1 / fps, duration);
      const frameSamples = Math.ceil((audioEndTime - audioStartTime) * sampleRate);
      
      if (frameSamples > 0) {
        const mixedAudio = mixAudioAtTime(
          audioStartTime,
          frameSamples,
          audioClips,
          assets,
          decodedAudio,
          sampleRate,
          channels,
          tracks,
          trackVolumes
        );
        
        // Encode audio in chunks of audioFrameSize
        const audioTimestamp = Math.round(audioStartTime * 1_000_000);
        
        try {
          const audioData = new AudioData({
            format: 'f32-planar',
            sampleRate,
            numberOfChannels: channels,
            numberOfFrames: mixedAudio[0].length,
            timestamp: audioTimestamp,
            data: interleaveAudioChannels(mixedAudio),
          });
          
          audioEncoder.encode(audioData);
          audioData.close();
        } catch (e) {
          // AudioData creation can fail, continue with video
          console.warn('Audio encoding error:', e);
        }
      }

      // Report progress (10-90% for rendering)
      onProgress(0.1 + (frameIndex / totalFrames) * 0.8);

      // Yield to keep UI responsive
      if (frameIndex % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Flush encoders
    onStatus('Finalizing...');
    await videoEncoder.flush();
    await audioEncoder.flush();

    // Create output blob with video and audio
    onStatus('Creating MP4 file...');
    const blob = await createMP4Blob(videoChunks, audioChunks, {
      width,
      height,
      fps,
      sampleRate,
      channels,
      videoCodec: videoEncoder.codec,
      audioCodec: audioEncoder.codec,
    });

    onProgress(1);
    onStatus('Export complete!');

    return blob;

  } finally {
    // Cleanup
    videoEncoder.close();
    audioEncoder.close();
    renderer.destroy();
    decodedAudio.clear();
  }
}

/**
 * Get clips that contribute audio (excludes muted tracks)
 */
function getAudioClips(clips, tracks, assets, mutedTracks = {}) {
  return clips.filter(clip => {
    const track = tracks.find(t => t.id === clip.trackId);
    if (!track) return false;
    if (track.type !== 'audio' && track.type !== 'video') return false;
    
    // Skip muted tracks
    if (mutedTracks[clip.trackId] === true) return false;
    
    const asset = assets[clip.assetId];
    if (!asset) return false;
    if (asset.type === 'image' || asset.isImage) return false;
    
    return true;
  });
}

/**
 * Mix audio from all active clips at a given time
 * Note: clips passed here should already be filtered to exclude muted tracks
 */
function mixAudioAtTime(startTime, numSamples, clips, assets, decodedAudio, sampleRate, channels, tracks, trackVolumes = {}) {
  // Create output buffers for each channel
  const output = [];
  for (let ch = 0; ch < channels; ch++) {
    output.push(new Float32Array(numSamples));
  }

  // Find clips active at this time
  for (const clip of clips) {
    const clipStart = clip.start;
    const clipEnd = clip.start + clip.duration;
    
    // Check if clip is active during this time range
    if (startTime >= clipEnd || startTime + numSamples / sampleRate <= clipStart) {
      continue;
    }

    const audioBuffer = decodedAudio.get(clip.assetId);
    if (!audioBuffer) continue;

    const trackVol = trackVolumes[clip.trackId] ?? 1;
    const baseGain = trackVol * (clip.gain ?? 1);
    if (baseGain === 0) continue;

    // Calculate sample positions
    const clipInSamples = Math.floor(clip.in * sampleRate);
    
    for (let i = 0; i < numSamples; i++) {
      const currentTime = startTime + i / sampleRate;
      
      // Check if this sample is within the clip's timeline range
      if (currentTime < clipStart || currentTime >= clipEnd) continue;
      
      // Apply fade in/out
      const fadeMultiplier = getFadeGain(clip, currentTime);
      const gain = baseGain * fadeMultiplier;
      
      // Calculate position in the source audio
      const timeInClip = currentTime - clipStart;
      const srcSample = clipInSamples + Math.floor(timeInClip * sampleRate);
      
      // Mix from each channel
      for (let ch = 0; ch < channels; ch++) {
        const srcChannel = ch < audioBuffer.numberOfChannels
          ? audioBuffer.getChannelData(ch)
          : audioBuffer.getChannelData(0);
        
        if (srcSample >= 0 && srcSample < srcChannel.length) {
          output[ch][i] += srcChannel[srcSample] * gain;
        }
      }
    }
  }

  // Clip to prevent distortion
  for (let ch = 0; ch < channels; ch++) {
    for (let i = 0; i < numSamples; i++) {
      output[ch][i] = Math.max(-1, Math.min(1, output[ch][i]));
    }
  }

  return output;
}

/**
 * Interleave audio channels for AudioData
 */
function interleaveAudioChannels(channelData) {
  const channels = channelData.length;
  const samples = channelData[0].length;
  
  // For f32-planar format, just concatenate channels
  const result = new Float32Array(samples * channels);
  for (let ch = 0; ch < channels; ch++) {
    result.set(channelData[ch], ch * samples);
  }
  return result;
}

/**
 * Create MP4 blob from encoded video and audio chunks
 */
async function createMP4Blob(videoChunks, audioChunks, options) {
  const { width, height, fps, sampleRate, channels, videoCodec, audioCodec } = options;
  
  try {
    const { createMuxer } = await import('./muxer');
    
    // Determine if we have audio
    const hasAudio = audioChunks.length > 0 && audioCodec;
    
    const muxer = createMuxer({ 
      width, 
      height, 
      fps,
      sampleRate,
      channels,
      videoCodec: videoCodec || 'avc1.640028',
      audioCodec: hasAudio ? audioCodec : null,
    });
    
    // Add all video chunks
    for (const { chunk, meta } of videoChunks) {
      muxer.addVideoChunk(chunk, meta);
    }
    
    // Add all audio chunks
    if (hasAudio) {
      for (const { chunk, meta } of audioChunks) {
        muxer.addAudioChunk(chunk, meta);
      }
    }
    
    return muxer.finalize();
  } catch (e) {
    console.error('MP4 muxing failed:', e);
    throw new Error(`Failed to create MP4 file: ${e.message}`);
  }
}

/**
 * Download a blob as a file
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The filename
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export just the audio as a WAV file
 * 
 * @param {Object} timeline - Timeline data from store.toJSON()
 * @param {Object} options - Export options
 * @returns {Promise<Blob>} The exported audio file
 */
export async function exportAudio(timeline, options = {}) {
  const {
    sampleRate = 48000,
    channels = 2,
    onProgress = () => {},
    onStatus = () => {},
    signal,
  } = options;

  const { tracks, clips, assets, mutedTracks = {}, trackVolumes = {} } = timeline;
  
  if (!clips || clips.length === 0) {
    throw new Error('No clips to export');
  }

  onStatus('Initializing audio export...');

  // Calculate timeline duration
  const duration = Math.max(...clips.map(c => c.start + c.duration));
  const totalSamples = Math.ceil(duration * sampleRate);

  // Create output buffer (interleaved)
  const outputBuffer = new Float32Array(totalSamples * channels);

  // Get audio clips (from both audio and video tracks, excluding muted tracks)
  const audioClips = clips.filter(clip => {
    const track = tracks.find(t => t.id === clip.trackId);
    if (!track) return false;
    if (track.type !== 'audio' && track.type !== 'video') return false;
    // Skip muted tracks
    if (mutedTracks[clip.trackId] === true) return false;
    const asset = assets[clip.assetId];
    if (!asset || asset.type === 'image' || asset.isImage) return false;
    return true;
  });

  onStatus(`Processing ${audioClips.length} audio clips...`);

  // Process each clip
  for (let i = 0; i < audioClips.length; i++) {
    if (signal?.aborted) {
      throw new DOMException('Export cancelled', 'AbortError');
    }

    const clip = audioClips[i];
    const asset = assets[clip.assetId];

    try {
      // Decode audio from asset
      const audioBuffer = await decodeAudioAsset(asset.url, sampleRate);
      
      // Mix into output buffer
      mixAudioClipToBuffer(outputBuffer, audioBuffer, clip, sampleRate, channels, trackVolumes);
    } catch (e) {
      console.warn(`Failed to process audio clip ${clip.id}:`, e);
    }

    onProgress((i + 1) / audioClips.length);
  }

  onStatus('Creating WAV file...');

  // Create WAV blob
  const wavBlob = createWavBlob(outputBuffer, sampleRate, channels);

  onStatus('Audio export complete!');

  return wavBlob;
}

/**
 * Decode an audio asset to an AudioBuffer
 */
async function decodeAudioAsset(url, sampleRate) {
  const response = await fetch(url);
  const arrayBuffer = await response.arrayBuffer();
  
  const audioContext = new (window.AudioContext || window.webkitAudioContext)({
    sampleRate,
  });
  
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  audioContext.close();
  
  return audioBuffer;
}

/**
 * Mix an audio clip into the output buffer (interleaved format)
 */
function mixAudioClipToBuffer(outputBuffer, audioBuffer, clip, sampleRate, channels, trackVolumes = {}) {
  const startSample = Math.floor(clip.start * sampleRate);
  const inSample = Math.floor(clip.in * sampleRate);
  const durationSamples = Math.floor(clip.duration * sampleRate);
  const trackVol = trackVolumes[clip.trackId] ?? 1;
  const baseGain = trackVol * (clip.gain ?? 1);

  for (let ch = 0; ch < channels; ch++) {
    const sourceChannel = ch < audioBuffer.numberOfChannels 
      ? audioBuffer.getChannelData(ch)
      : audioBuffer.getChannelData(0); // Mono fallback

    for (let i = 0; i < durationSamples; i++) {
      const srcIndex = inSample + i;
      const dstIndex = (startSample + i) * channels + ch;
      
      if (srcIndex < sourceChannel.length && dstIndex < outputBuffer.length) {
        // Calculate timeline time for this sample
        const timelineTime = clip.start + (i / sampleRate);
        // Apply fade in/out
        const fadeMultiplier = getFadeGain(clip, timelineTime);
        const gain = baseGain * fadeMultiplier;
        
        outputBuffer[dstIndex] += sourceChannel[srcIndex] * gain;
      }
    }
  }
}

/**
 * Create a WAV blob from audio data
 */
function createWavBlob(samples, sampleRate, channels) {
  const bytesPerSample = 2; // 16-bit
  const blockAlign = channels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = samples.length * bytesPerSample;
  const bufferSize = 44 + dataSize;

  const buffer = new ArrayBuffer(bufferSize);
  const view = new DataView(buffer);

  // WAV header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, bufferSize - 8, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM format chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, channels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Convert float samples to 16-bit PCM
  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, sample * 0x7FFF, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view, offset, string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}
